/**
 * El salto desde Unity 360 a Finance 360 (ítem 058 de la suite).
 *
 * ── QUE ES, Y POR QUE FALTABA ────────────────────────────────────────────────
 *
 * No es una pantalla: es el pasillo. Quien ya entró a Unity toca «Finanzas» en
 * su menú de portales y cae acá, y en lo que tarda un parpadeo está dentro de
 * Finance sin escribir la clave otra vez.
 *
 * **La puerta del servidor existía desde el ítem 034 y nadie la llamaba.**
 * `POST /suite/entrar` verifica la identidad firmada por Unity, comprueba que la
 * persona sea usuaria activa de este cliente y emite una sesión de Finance —
 * completo y probado—, pero no había ninguna página que la usara. Medido el
 * 07/10: cero llamadas en los tres repositorios. Una puerta sin camino.
 *
 * ── POR QUE EL PASE NO VIENE EN LA DIRECCION ────────────────────────────────
 *
 * Porque una dirección queda en el registro de nginx, en el historial del
 * navegador, en la cabecera `Referer` de la primera imagen que cargue la página
 * y en el portapapeles de cualquiera que comparta el enlace. Un pase en la URL
 * es un pase publicado.
 *
 * Así que esta página llega **sin nada** y lo pide: le avisa a la ventana que la
 * abrió que está lista, y Unity le entrega la identidad por `postMessage`, que
 * no se registra en ninguna parte. Es el mismo protocolo del embebido
 * (`EmbedShell`) y del salto a Payroll, con `window.opener` en lugar de
 * `window.parent`. Tercer uso, un solo protocolo.
 *
 * ── LO QUE SI ES DISTINTO DE PAYROLL, Y HAY QUE SABERLO ─────────────────────
 *
 * Payroll resuelve la empresa **por subdominio** en todas sus puertas, así que
 * su pasillo no necesita que nadie le diga a qué cliente entra. Finance resuelve
 * el inquilino por el `tenantSlug` que recibe, así que el salto tiene que
 * traerlo. Viaja **en el mismo mensaje que el pase** y no en la dirección: no es
 * un secreto —es el nombre del cliente— pero ponerlo en la URL dejaría la
 * dirección con un dato que después hay que explicar, y abriría una segunda
 * forma de decir a qué empresa se entra.
 *
 * ── Y DISTINTO DEL EMBEBIDO, QUE ESTA AL LADO ───────────────────────────────
 *
 * El embebido abre UNA pantalla dentro de Unity, con la empresa ya elegida por
 * Unity, y su sesión vive en memoria para no pisarle la sesión a quien tenga
 * Finance abierto en otra pestaña. Esto abre **Finance entero**, en su propia
 * pestaña, con la sesión guardada como la del login normal: la persona viene a
 * trabajar acá, y recargar no la puede echar.
 *
 * ── LO QUE PASA SI ALGUIEN LLEGA ACA SOLO ───────────────────────────────────
 *
 * Sin ventana que la haya abierto no hay a quién pedirle el pase, y se dice
 * exactamente eso con el camino de vuelta. No se adivina ni se deja una rueda
 * girando para siempre.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import useAuthStore from '../../store/authStore';
import { aplicarTemaEmbebido } from '../../utils/temaEmbebido';

/** Si Unity no contesta, no se deja la rueda girando para siempre. */
const ESPERA_MAXIMA_MS = 20000;

export default function EntrarDesdeUnity () {
  const navigate = useNavigate();
  const abrirSesionDesdeUnity = useAuthStore((s) => s.abrirSesionDesdeUnity);

  const [estado, setEstado] = useState('esperando');   // esperando · entrando · error
  const [mensaje, setMensaje] = useState('');
  const anfitrion = useRef(null);
  const entrando = useRef(false);

  const fallar = useCallback((texto) => {
    setEstado('error');
    setMensaje(texto);
  }, []);

  /** Cambia la identidad firmada por Unity por una sesión de Finance. */
  const entrar = useCallback(async (identidad, inquilino) => {
    if (entrando.current) return;
    entrando.current = true;
    setEstado('entrando');
    try {
      if (!inquilino) {
        throw new Error('Unity no indicó a qué cliente de Finance entrar. '
          + 'Revise la conexión con Finance 360 en Unity.');
      }
      // La base del canje NO es la del resto de la aplicación: `/suite` vive
      // fuera de `/api/v1` porque lo llama el navegador sin credencial de API.
      const base = (import.meta.env.VITE_API_URL || '').replace(/\/api\/v1\/?$/, '');
      const { data } = await axios.post(`${base}/suite/entrar`,
        { token: identidad, tenantSlug: inquilino },
        { headers: { 'Content-Type': 'application/json' } });

      const s = data?.data;
      if (!s?.token) throw new Error('El canje no devolvió sesión');

      abrirSesionDesdeUnity({
        user: s.user,
        token: s.token,
        emisores: s.emisores,
        tenantSlug: s.tenantSlug || inquilino,
        emisorId: s.emisorId
      });

      // Ya está dentro: se suelta la referencia a quien la abrió. El precio de
      // `opener` se paga acá — el resto de la vida de la pestaña ya no la tiene.
      if (anfitrion.current) {
        try { window.opener?.postMessage({ tipo: 'suite:abierto' }, anfitrion.current); } catch { /* ya se cerró */ }
        anfitrion.current = null;
      }

      navigate('/dashboard', { replace: true });
    } catch (e) {
      const dato = e.response?.data;
      fallar(dato?.error || dato?.message || e.message || 'No se pudo abrir la sesión');
    } finally {
      entrando.current = false;
    }
  }, [abrirSesionDesdeUnity, navigate, fallar]);

  useEffect(() => {
    const abridor = window.opener;
    if (!abridor || abridor === window) {
      fallar('Esta dirección se abre desde Unity 360, desde el menú de portales. '
        + 'Si llegó acá directo, entre por la pantalla de ingreso de Finance.');
      return;
    }

    const alLlegar = (evento) => {
      const dato = evento.data;
      if (!dato || typeof dato !== 'object' || typeof dato.tipo !== 'string') return;
      if (!dato.tipo.startsWith('unity:')) return;
      // Solo se le escucha a quien abrió esta pestaña, y su origen se fija con el
      // primer mensaje válido. Sin las dos comprobaciones, cualquier ventana con
      // una referencia a esta podría meterle una identidad.
      if (evento.source !== abridor) return;
      if (anfitrion.current && evento.origin !== anfitrion.current) return;
      if (!anfitrion.current) anfitrion.current = evento.origin;

      if (dato.tipo === 'unity:tema') { aplicarTemaEmbebido(dato.tema); return; }
      if (dato.tipo !== 'unity:identidad') return;

      // Los colores del anfitrión vienen con el pase, para que el salto no se
      // vea como un corte entre dos aplicaciones (ítem 038).
      aplicarTemaEmbebido(dato.tema);
      if (!dato.token) { fallar('Unity no entregó la identidad'); return; }
      entrar(dato.token, dato.inquilino);
    };

    window.addEventListener('message', alLlegar);
    // Se avisa DESPUES de empezar a escuchar: al revés, si Unity contesta al
    // instante el mensaje se pierde y la pestaña queda esperando para siempre.
    // Va a '*' porque no lleva ningún dato: no revela nada.
    abridor.postMessage({ tipo: 'suite:listo' }, '*');

    const reloj = setTimeout(() => {
      if (!entrando.current) {
        fallar('Unity 360 no entregó el pase. Vuelva a intentarlo desde su menú de portales.');
      }
    }, ESPERA_MAXIMA_MS);

    return () => { window.removeEventListener('message', alLlegar); clearTimeout(reloj); };
  }, [entrar, fallar]);

  if (estado === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center p-8 bg-gray-50">
        <div className="max-w-md text-center">
          <h1 className="text-lg font-semibold text-gray-900">No se pudo entrar</h1>
          <p className="mt-2 text-sm text-gray-600">{mensaje}</p>
          <a href="/login" className="inline-block mt-5 text-sm font-medium text-blue-700 hover:text-blue-800">
            Ir a la pantalla de ingreso
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-gray-200 border-t-blue-600 rounded-full animate-spin mx-auto mb-5" />
        <p className="text-sm text-gray-600">
          {estado === 'entrando' ? 'Abriendo Finance 360…' : 'Entrando desde Unity 360…'}
        </p>
      </div>
    </div>
  );
}
